import { AnalyticsEventNameType, DataSource, DataSourceType } from "@coeff/api";
import {
  AuthorizeAmplitudeContent,
  AuthorizeBigQueryContent,
  AuthorizeLinkedInContent,
  AuthorizeLookerContent,
  AuthorizePPressContent,
  AuthorizeSQLContent,
  AuthorizeTableauContent,
  AuthContextValue,
  AuthProvider as SharedAuthProvider,
  buildPPressMetadata,
  getDataSourceApiErrorMessage,
  getOAuthAuthorizationUrl,
  PPress,
  PpressContext,
  DataSourceChangeConfirmationContent,
  DataSourceChangeConfirmationLocationState,
} from "@coeff/components";
import { AuthType, DATA_SOURCE_AUTH_TYPE } from "@coeff/utils";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { AppState } from "src/store";
import styled from "styled-components";

import { config } from "../config";
import { useClientFlagsQuery } from "../hooks/useClientFlagsQuery";
import { useApiContext, useTrack } from "../utils/contexts";

import { ConnectionHelpCard } from "./ConnectionHelpCard";
import { LoadingRoute } from "./LoadingRoute";

declare const __LOCAL_DEVELOPMENT_NGROK_API_URL__: string;

const { COLORS } = require("@coeff/utils");
const { Content, Footer } = require("antd").Layout;

const StyledLayout = styled(require("antd").Layout)`
  && {
    height: 100%;
    min-height: 100vh;
    background-color: ${COLORS.white};
  }
`;

const StyledContent = styled(Content)`
  && {
    height: 100%;
    min-height: calc(100vh - 128px);
    margin-top: 0;
    background-color: ${COLORS.white};
    padding: 14px 0px;

    .container {
      max-width: 400px;
      margin: 0 auto;
      padding: 20px;

      .ant-radio-checked .ant-radio-inner::after {
        left: 50%;
        top: 50%;
      }
    }
  }
`;

const StyledFooter = styled(Footer)`
  && {
    position: sticky;
    bottom: 0px;
    padding: 0;
    background: ${COLORS.white};
    z-index: 50;
  }
`;

const AUTH_SUCCESS_REDIRECT_PATH = "/oauth_attempt?status=success&from=webapp";

const completePostDataSourceCreation = (config: {
  dataSource: DataSource;
  onComplete: (dataSource: DataSource) => void;
}) => {
  config.onComplete(config.dataSource);
};

const UnsupportedMessage = styled.div`
  padding: 40px 20px;
  text-align: center;
  line-height: 1.5;

  h2 {
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 12px;
    color: ${COLORS.black85};
  }

  p {
    font-size: 14px;
    color: ${COLORS.black45};
    margin-bottom: 8px;
  }

  a {
    color: ${COLORS.coeblue4};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

/**
 * AuthPage - Generic authorization page for new connections (/auth/:dataSourceType?)
 * Supports BigQuery, Amplitude, Looker, Tableau, LinkedIn, and PPress connectors
 */
export const AuthPage: React.FC = () => {
  const { dataSourceType } = useParams<{ dataSourceType?: string }>();
  const history = useHistory();
  const { data: clientFlags, isLoading: isLoadingClientFlags } = useClientFlagsQuery();
  const userEmail = useSelector((state: AppState) => state.app.email);
  const { apiClient } = useApiContext();
  const track = useTrack();

  // Data source change confirmation state
  const [changeConfirmationRequest, setChangeConfirmationRequest] =
    useState<DataSourceChangeConfirmationLocationState>();

  // PPress/OAuth auth flow state — drives the loading + Cancel button on the
  // authorize button while the OAuth popup is open.
  const [isAuthInProgress, setIsAuthInProgress] = useState(false);
  const [isAuthCompleted, setIsAuthCompleted] = useState(false);

  const handleUpserted = () => history.replace(AUTH_SUCCESS_REDIRECT_PATH);

  useEffect(() => {
    if (isLoadingClientFlags || !clientFlags || !dataSourceType) {
      return;
    }

    track(AnalyticsEventNameType.WebappAuthPageViewed, {
      event_from: "webapp",
      import_data_source: dataSourceType,
      conn_data_source: dataSourceType,
    });
  }, [clientFlags, dataSourceType, isLoadingClientFlags, track]);

  if (isLoadingClientFlags || !clientFlags) {
    return <LoadingRoute />;
  }

  const coeffDevRedirectBaseUrl = __LOCAL_DEVELOPMENT_NGROK_API_URL__ || undefined;

  // Create auth context value for shared library
  const authValue: AuthContextValue = {
    apiClient,
    track: (eventName, eventProps) => track(eventName, eventProps),
    eventProps: {
      email: userEmail,
      domain: userEmail?.split("@")[1],
    },
    clientFlags,
    sentryCapture: undefined, // Webapp has its own sentry setup
    router: {
      back: () => history.goBack(),
      push: path => history.push(path),
    },
    components: {
      PpressContext,
    },
    analyticsContext: {
      fromConnectedSource: false,
      fromOnboarding: false,
    },
    coeffDevRedirectBaseUrl,
    // Lets PPress-namespace connectors (e.g. Tableau's embedded username/key-pair form)
    // navigate on success when they create a data source directly via the AuthContext.
    navigateOnDataSourceUpsert: handleUpserted,
    initPostDataSourceCreation: completePostDataSourceCreation,
    renderConnectionHelpCard: ({ dataSourceType: helpDataSourceType }) => (
      <ConnectionHelpCard dataSourceType={helpDataSourceType} />
    ),
    // Utility functions for OAuth and PPress
    getOAuthAuthorizationUrl: (api, params) =>
      getOAuthAuthorizationUrl(api, { ...params, from: "webapp", coeffDevRedirectBaseUrl }),
    buildPPressMetadata,
    getDataSourceApiErrorMessage,
  };

  const authType = dataSourceType ? DATA_SOURCE_AUTH_TYPE[dataSourceType] : undefined;

  const dataSourceName = dataSourceType
    ? dataSourceType.charAt(0).toUpperCase() + dataSourceType.slice(1)
    : "this data source";

  const renderAuthComponent = () => {
    if (!dataSourceType || !authType) {
      return null;
    }

    const commonEventProps = {
      email: userEmail,
      domain: userEmail?.split("@")[1],
    };

    const renderPPressContent = () => (
      <AuthorizePPressContent
        isModal
        isAuthCompleted={isAuthCompleted}
        setIsAuthCompleted={setIsAuthCompleted}
        isAuthInProgress={isAuthInProgress}
        setIsAuthInProgress={setIsAuthInProgress}
        dataSourceId={undefined}
        dataSourceType={dataSourceType as DataSourceType}
        dataImport={undefined}
        fromConnectedSource={false}
        fromOnboarding={false}
        onChangeConfirmationRequired={confirmationState => {
          setChangeConfirmationRequest(confirmationState);
        }}
        onDataSourceUpserted={handleUpserted}
        initPostDataSourceCreation={completePostDataSourceCreation}
        userEmail={userEmail}
        clientFlags={clientFlags}
        event_props={commonEventProps}
        webappBaseUrl={config.BASE_URL}
        PPress={PPress}
      />
    );

    // Route based on auth type, matching the addon's AUTHORIZE_SCREENS mapping.
    switch (authType) {
      case AuthType.SQL:
        if (dataSourceType === "bigquery") {
          return (
            <AuthorizeBigQueryContent
              dataSource={undefined}
              isEditConnectionId=""
              fromConnectedSource={false}
              fromOnboarding={false}
              event_props={commonEventProps}
              onComplete={handleUpserted}
              navigateOnDataSourceUpsert={handleUpserted}
              onCancel={() => history.goBack()}
            />
          );
        }
        // databricks, motherduck and arrow_flight_sql are backend-driven: their
        // auth UI (JDBC/PAT banner, OAuth toggle, OAuth config cards) is rendered
        // as PPress JSX that embeds <PPress.AuthorizeSQL .../>, so they must go
        // through AuthorizePPressContent. Only the plain warehouses (mysql,
        // postgres, redshift, mssql) render the bare SQL form directly, matching
        // the addon's direct authorize* routes.
        if (
          dataSourceType === "databricks" ||
          dataSourceType === "motherduck" ||
          dataSourceType === "arrow_flight_sql"
        ) {
          return renderPPressContent();
        }
        return (
          <AuthorizeSQLContent
            dataSourceType={dataSourceType as DataSourceType}
            dataSource={undefined}
            fromConnectedSource={false}
            fromOnboarding={false}
            onDataSourceUpserted={handleUpserted}
            onCancel={() => history.goBack()}
            initPostDataSourceCreation={completePostDataSourceCreation}
            renderConnectionHelpCard={({ dataSourceType: helpDataSourceType }) => (
              <ConnectionHelpCard dataSourceType={helpDataSourceType} />
            )}
          />
        );

      case AuthType.AMPLITUDE:
        return (
          <AuthorizeAmplitudeContent
            isModal
            dataSourceId={undefined}
            fromConnectedSource={false}
            fromOnboarding={false}
            event_props={commonEventProps}
            onDataSourceUpserted={handleUpserted}
            onCancel={() => history.goBack()}
            initPostDataSourceCreation={completePostDataSourceCreation}
          />
        );

      case AuthType.LOOKER:
        // Non-OAuth Looker - uses API credentials
        return (
          <AuthorizeLookerContent
            isModal
            dataSourceId={undefined}
            dataSource={undefined}
            clientFlags={clientFlags}
            event_props={commonEventProps}
            webappBaseUrl={config.BASE_URL}
            onDataSourceUpserted={handleUpserted}
            onCancel={() => history.goBack()}
          />
        );

      case AuthType.TABLEAU:
        return (
          <AuthorizeTableauContent
            dataSourceId={undefined}
            dataSource={undefined}
            event_props={commonEventProps}
            onDataSourceUpserted={handleUpserted}
            onCancel={() => history.goBack()}
            initPostDataSourceCreation={completePostDataSourceCreation}
          />
        );

      case AuthType.OAUTH:
        // Handles linkedin, google_drive, google_sheets, excel, google_analytics
        if (dataSourceType === "linkedin") {
          return (
            <AuthorizeLinkedInContent
              dataSource={undefined}
              isEditConnectionId=""
              browserAgentExtensionUrl={config.BROWSER_AGENT_EXTENSION_URL}
              onComplete={handleUpserted}
            />
          );
        }
        // Other OAUTH connectors not yet supported in webapp
        return null;

      case AuthType.PPRESS:
        // snowflake and mongodb are PPRESS connectors whose auth UI (description
        // banner, OAuth toggle, help card) is backend-driven JSX that embeds
        // <PPress.AuthorizeSQL .../>, handled the same as every other PPress
        // connector via AuthorizePPressContent.
        return renderPPressContent();

      default:
        return null;
    }
  };

  const authComponent = renderAuthComponent();

  // Render change confirmation screen if needed
  if (changeConfirmationRequest) {
    return (
      <StyledLayout>
        <StyledContent>
          <div className="container">
            <DataSourceChangeConfirmationContent
              style={{ padding: "16px", maxWidth: "600px", margin: "0 auto" }}
              existingDataSourceUniqueId={changeConfirmationRequest.existingDataSourceUniqueId}
              newDataSourceUniqueId={changeConfirmationRequest.newDataSourceUniqueId}
              dataSourceType={changeConfirmationRequest.dataSourceType}
              dataSourceId={changeConfirmationRequest.dataSourceId}
              ppressMetadata={changeConfirmationRequest.ppressMetadata}
              credentials={changeConfirmationRequest.credentials}
              additionalDisplayFields={changeConfirmationRequest.additionalDisplayFields}
              api={apiClient}
              track={(eventName: string, properties?: Record<string, any>) =>
                track(eventName as any, properties)
              }
              onSuccess={() => {
                setChangeConfirmationRequest(undefined);
                handleUpserted();
              }}
              onError={() => {
                console.error("Account change failed. Please try again.");
              }}
              onCancel={() => {
                setChangeConfirmationRequest(undefined);
              }}
            />
          </div>
        </StyledContent>
      </StyledLayout>
    );
  }

  return (
    <StyledLayout>
      <StyledContent>
        <div className="container">
          {authComponent ? (
            <SharedAuthProvider value={authValue}>{authComponent}</SharedAuthProvider>
          ) : (
            <UnsupportedMessage>
              <h2>Authorization Not Available</h2>
              <p>
                Authorization for {dataSourceName} is not supported in the web app at the moment.
              </p>
              <p>Please use the Google Sheets add-on to connect this data source.</p>
              <a href="https://docs.coefficient.com" target="_blank" rel="noopener noreferrer">
                Learn more
              </a>
            </UnsupportedMessage>
          )}
        </div>
      </StyledContent>
      <StyledFooter />
    </StyledLayout>
  );
};

export default AuthPage;
